import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';
import { Button, FooterActionBar, Icon } from '@saviynt/design-system';

import {
  SodInsights,
  type SodInsightsHandle,
} from '../../../../shared/components';
import { RENDER_MODES } from '../../../../shared/constants';
import { useTypedSelector } from '../../../../shared/hooks';
import { type InsightItemTypes } from '../../../../shared/types';
import { violationsTabActions } from '../../data-store/violationsTabSlice';
import { useGetInsights } from '../../hooks';

import './ViolationsInsights.css';

interface ViolationsInsightsProps {
  className?: string;
}

/**
 * ViolationsInsights
 */
function ViolationsInsights({ className }: ViolationsInsightsProps) {
  const dispatch = useDispatch();
  const insightsRef = useRef<SodInsightsHandle>(null);

  const {
    aag: {
      global: { isGlobalLoading },
      violationsTab: {
        controls: {
          renderMode,
          isInsightsOpen,
          activeFilters,
          tabInAction,
          searchKey,
          sortInAction,
          activeInsight,
        },
      },
    },
  } = useTypedSelector((state) => state);

  const [insightsData, setInsightsData] = useState<InsightItemTypes[] | null>(
    null
  );

  const { initializeInsightsFetch, isError, isLoading } = useGetInsights();

  const handleInsightsFetch = useCallback(() => {
    if (!isGlobalLoading) {
      setInsightsData(null);

      initializeInsightsFetch(
        activeFilters,
        tabInAction,
        searchKey,
        sortInAction
      )
        .then((response) => {
          const typedResponse = response as InsightItemTypes[];

          setInsightsData(typedResponse);
        })
        .catch((error) => {
          Logger.log('error', error);
        });
    }
  }, [
    isGlobalLoading,
    activeFilters,
    tabInAction,
    searchKey,
    sortInAction,
    initializeInsightsFetch,
  ]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      insightsRef.current?.home();
      handleInsightsFetch();
    }, 20);

    return () => clearTimeout(debounceTimer);
  }, [handleInsightsFetch]);

  // Insights inbuilt actions
  const handleIsInsights = useCallback(() => {
    dispatch(violationsTabActions.setIsInsightsOpen(!isInsightsOpen));
  }, [dispatch, isInsightsOpen]);

  const handleOptionClick = useCallback(
    (insightItem: InsightItemTypes | null) => {
      dispatch(violationsTabActions.setActiveInsight(insightItem));
    },
    [dispatch]
  );

  // Insights footer when selected
  const isGraphMode = useMemo(
    () => Boolean(renderMode === RENDER_MODES.GRAPH),
    [renderMode]
  );

  // Commenting for future reference
  // const [fetchGraphCluster] =
  //   violationsTabActions.useLazyGetViolationsGraphCusterQuery();

  // useEffect(() => {
  //   if (activeGraphPoint) {
  //     fetchGraphCluster(activeGraphPoint.clusterId)
  //       .unwrap()
  //       .then((res) => {
  //         const row = getRowContext({
  //           ...res.data[0],
  //           tracker: { id: res.data[0].sodKey },
  //         });

  //         console.log('row', { row, res });

  //         dispatch(violationsTabActions.setRowContext({ current: row }));
  //         dispatch(violationsTabActions.setIsSidePanelVisible(true));
  //       })
  //       .catch((error) => {
  //         console.log('error', error);
  //       });
  //   }
  // }, [dispatch, fetchGraphCluster, activeGraphPoint]);

  const renderFooterContent = useCallback(
    () => (
      <FooterActionBar className='Sod-ViolationsInsights-footer'>
        <Button
          onClick={() => {
            if (isGraphMode) {
              dispatch(violationsTabActions.setRenderMode(RENDER_MODES.LIST));

              return;
            }

            dispatch(violationsTabActions.setRenderMode(RENDER_MODES.GRAPH));
          }}
          kind='subtle'
          leftIcon={
            isGraphMode ? <Icon kind='List' /> : <Icon kind='Analyze' />
          }>
          {isGraphMode ? 'View List' : 'View Graph'}
        </Button>
      </FooterActionBar>
    ),
    [dispatch, isGraphMode]
  );

  return (
    isInsightsOpen && (
      <SodInsights
        ref={insightsRef}
        data={insightsData}
        isLoading={isLoading || (!insightsData && !isError)}
        isError={isError}
        activeInsight={activeInsight}
        isInsightsOpen={isInsightsOpen}
        setIsInsightsOpen={handleIsInsights}
        handleRefresh={() => handleInsightsFetch()}
        handleOptionClick={handleOptionClick}
        footerContent={renderFooterContent()}
        className={className}
      />
    )
  );
}

ViolationsInsights.defaultProps = {
  className: null,
};

export default ViolationsInsights;
